import React, { useState } from 'react';
import { BookOpen, Play, RotateCcw, Smartphone, Monitor, Zap, Star, Heart } from 'lucide-react';
import { testArticles, testNewsletters, testQueueItems, testReadingStats, getRandomArticle, getRandomNewsletter } from '../../utils/testContent';
import EnhancedArticleReader from './EnhancedArticleReader';
import EnhancedReadingQueue from './EnhancedReadingQueue';
import SmartDiscoverySystem from './SmartDiscoverySystem';
import MobileReadingExperience from './MobileReadingExperience';

const ReaderTestPage: React.FC = () => {
  const [activeTest, setActiveTest] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [showMobileReader, setShowMobileReader] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const testScenarios = [
    {
      id: 'enhanced-reader',
      title: 'Enhanced Article Reader',
      description: 'Test the immersive reading experience with customizable settings',
      icon: BookOpen,
      color: 'blue',
      component: 'reader'
    },
    {
      id: 'reading-queue',
      title: 'Enhanced Reading Queue',
      description: 'Test the smart queue management with multiple view modes',
      icon: Play,
      color: 'green',
      component: 'queue'
    },
    {
      id: 'discovery-system',
      title: 'Smart Discovery System',
      description: 'Test AI-powered recommendations and advanced filtering',
      icon: Zap,
      color: 'purple',
      component: 'discovery'
    },
    {
      id: 'mobile-experience',
      title: 'Mobile Reading Experience',
      description: 'Test mobile-optimized reading with gestures and touch controls',
      icon: Smartphone,
      color: 'orange',
      component: 'mobile'
    },
    {
      id: 'full-dashboard',
      title: 'Complete Reader Dashboard',
      description: 'Test the integrated reader experience with all features',
      icon: Monitor,
      color: 'indigo',
      component: 'dashboard'
    }
  ];

  const handleTestArticle = (articleId: string) => {
    const article = testArticles.find(a => a.id === articleId) || getRandomArticle();
    setSelectedArticle(article);
    
    if (activeTest === 'mobile-experience') {
      setShowMobileReader(true);
    }
  };

  const handleBookmark = (articleId: string, bookmarked: boolean) => {
    };

  const handleShare = (articleId: string) => {
    };

  const handleDownload = (articleId: string) => {
    };

  const handleLike = (articleId: string, liked: boolean) => {
    };

  const handleStar = (articleId: string, starred: boolean) => {
    };

  const handleFlag = (articleId: string, flagged: boolean) => {
    };

  const handleSubscribe = (newsletterId: string) => {
    };

  const handlePreview = (newsletterId: string) => {
    };

  const handleViewProfile = (authorId: string) => {
    };

  const handleRemoveFromQueue = (articleId: string) => {
    };

  const handleUpdatePriority = (articleId: string, priority: 'urgent' | 'normal' | 'low') => {
    };

  const handleStarArticle = (articleId: string, starred: boolean) => {
    };

  const handleArchiveArticle = (articleId: string) => {
    };

  const toggleMobile = () => {
    setIsMobile(!isMobile);
  };

  const renderTestComponent = () => {
    switch (activeTest) {
      case 'enhanced-reader':
        return selectedArticle ? (
          <EnhancedArticleReader
            article={selectedArticle}
            onClose={() => setSelectedArticle(null)}
            onBookmark={handleBookmark}
            onShare={handleShare}
            onDownload={handleDownload}
            isBookmarked={selectedArticle.isBookmarked}
          />
        ) : (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Select an Article to Test
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Choose an article from the list below to test the enhanced reader
            </p>
            <div className="space-y-2">
              {testArticles.slice(0, 3).map((article) => (
                <button
                  key={article.id}
                  onClick={() => handleTestArticle(article.id)}
                  className="block w-full text-left p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                >
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{article.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{article.author} • {article.readTime} min read</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 'reading-queue':
        return (
          <EnhancedReadingQueue
            userId="test-user"
            onReadArticle={handleTestArticle}
            onRemoveFromQueue={handleRemoveFromQueue}
            onUpdatePriority={handleUpdatePriority}
            onStarArticle={handleStarArticle}
            onArchiveArticle={handleArchiveArticle}
          />
        );

      case 'discovery-system':
        return (
          <SmartDiscoverySystem
            userId="test-user"
            onSubscribe={handleSubscribe}
            onPreview={handlePreview}
            onViewProfile={handleViewProfile}
          />
        );

      case 'mobile-experience':
        return selectedArticle ? (
          <MobileReadingExperience
            article={selectedArticle}
            onClose={() => {
              setShowMobileReader(false);
              setSelectedArticle(null);
            }}
            onNext={() => {
              const currentIndex = testArticles.findIndex(a => a.id === selectedArticle.id);
              const nextArticle = testArticles[(currentIndex + 1) % testArticles.length];
              setSelectedArticle(nextArticle);
            }}
            onPrevious={() => {
              const currentIndex = testArticles.findIndex(a => a.id === selectedArticle.id);
              const prevIndex = currentIndex === 0 ? testArticles.length - 1 : currentIndex - 1;
              setSelectedArticle(testArticles[prevIndex]);
            }}
            onBookmark={handleBookmark}
            onShare={handleShare}
            onDownload={handleDownload}
            onLike={handleLike}
            onStar={handleStar}
            onFlag={handleFlag}
            hasNext={true}
            hasPrevious={true}
          />
        ) : (
          <div className="text-center py-12">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Mobile Reading Experience
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This will open the mobile-optimized reading experience
            </p>
            <button
              onClick={() => handleTestArticle('1')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Mobile Reading Test
            </button>
          </div>
        );

      case 'full-dashboard':
        return (
          <div className="space-y-8">
            {/* Reading Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Articles Read</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{testReadingStats.totalArticlesRead}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reading Time</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{testReadingStats.totalReadingTime}m</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <Play className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reading Streak</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{testReadingStats.readingStreak} days</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                    <Star className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Goal Progress</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {testReadingStats.goalProgress}/{testReadingStats.readingGoal}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(testReadingStats.goalProgress / testReadingStats.readingGoal) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { title: 'Continue Reading', description: 'Pick up where you left off', icon: BookOpen, color: 'blue' },
                  { title: 'Discover New', description: 'Find your next favorite newsletter', icon: Zap, color: 'purple' },
                  { title: 'Reading Goal', description: `${testReadingStats.goalProgress}/${testReadingStats.readingGoal} articles this month`, icon: Star, color: 'green' },
                  { title: 'My Subscriptions', description: 'Manage your newsletter subscriptions', icon: Heart, color: 'red' }
                ].map((action, index) => (
                  <button
                    key={index}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 bg-${action.color}-100 dark:bg-${action.color}-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <action.icon className={`w-5 h-5 text-${action.color}-600 dark:text-${action.color}-400`} />
                      </div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{action.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Test Articles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Test Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testArticles.slice(0, 3).map((article) => (
                  <div
                    key={article.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-200"
                  >
                    {article.imageUrl && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          article.priority === 'urgent' ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400' :
                          article.priority === 'normal' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {article.priority}
                        </span>
                        <div className="flex items-center gap-1">
                          <button className="p-1 text-gray-400 hover:text-yellow-500 rounded transition-colors">
                            <Star className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-3">
                        <span>{article.author}</span>
                        <span>{article.readTime} min</span>
                      </div>
                      <button
                        onClick={() => handleTestArticle(article.id)}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Read Article
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Select a Test Scenario
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Choose a test scenario from the sidebar to explore the reader experience features
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-gray-100">Reader Experience Test Suite</span>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={toggleMobile}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isMobile 
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {isMobile ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
                {isMobile ? 'Mobile' : 'Desktop'}
              </button>
              <button
                onClick={() => setActiveTest(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Test Scenarios</h2>
            <div className="space-y-2">
              {testScenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  onClick={() => setActiveTest(scenario.id)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${
                    activeTest === scenario.id
                      ? `bg-${scenario.color}-100 text-${scenario.color}-600 dark:bg-${scenario.color}-900 dark:text-${scenario.color}-400`
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <scenario.icon className="w-5 h-5" />
                    <div>
                      <h3 className="font-medium">{scenario.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {renderTestComponent()}
        </div>
      </div>

      {/* Mobile Reader Modal */}
      {showMobileReader && selectedArticle && (
        <MobileReadingExperience
          article={selectedArticle}
          onClose={() => {
            setShowMobileReader(false);
            setSelectedArticle(null);
          }}
          onNext={() => {
            const currentIndex = testArticles.findIndex(a => a.id === selectedArticle.id);
            const nextArticle = testArticles[(currentIndex + 1) % testArticles.length];
            setSelectedArticle(nextArticle);
          }}
          onPrevious={() => {
            const currentIndex = testArticles.findIndex(a => a.id === selectedArticle.id);
            const prevIndex = currentIndex === 0 ? testArticles.length - 1 : currentIndex - 1;
            setSelectedArticle(testArticles[prevIndex]);
          }}
          onBookmark={handleBookmark}
          onShare={handleShare}
          onDownload={handleDownload}
          onLike={handleLike}
          onStar={handleStar}
          onFlag={handleFlag}
          hasNext={true}
          hasPrevious={true}
        />
      )}
    </div>
  );
};

export default ReaderTestPage;
